import { Injectable } from '@angular/core';
import { ConfigService } from '@shared/services/config.service';
import { LDClient, initialize } from 'launchdarkly-js-client-sdk';
import { BehaviorSubject } from 'rxjs';
import { environment } from '../../environments/environment';
import { AuthService, EUAV3UserInfo } from '@shared/auth/auth.service';

export const FLAG_KEYS = {
  KILL_SWITCH: 'site-kill-switch',
  GENERATION_TOGGLE_ENABLED: 'generation-toggle',
  TOPIC_AND_FILTERING: 'topic-filtering',
  CC_FIELD_ENABLED: 'CCEmail',
} as const;

export type AppFeatureFlags = {
  [FLAG_KEYS.KILL_SWITCH]: boolean;
  [FLAG_KEYS.GENERATION_TOGGLE_ENABLED]: boolean;
  [FLAG_KEYS.TOPIC_AND_FILTERING]: boolean;
  [FLAG_KEYS.CC_FIELD_ENABLED]: boolean;
};

const DEFAULT_FEATURE_FLAGS: AppFeatureFlags = {
  [FLAG_KEYS.KILL_SWITCH]: false,
  [FLAG_KEYS.GENERATION_TOGGLE_ENABLED]: false,
  [FLAG_KEYS.TOPIC_AND_FILTERING]: false,
  [FLAG_KEYS.CC_FIELD_ENABLED]: false,
};

@Injectable({
  providedIn: 'root',
})
export class FeatureFlagService {
  private launchDarkly!: LDClient;
  private readonly featureFlagsSubject$: BehaviorSubject<AppFeatureFlags> = new BehaviorSubject<AppFeatureFlags>(DEFAULT_FEATURE_FLAGS);

  constructor(
    private readonly authService: AuthService,
    private readonly config: ConfigService,
  ) {
    if (!environment.ci) {
      this.authService.pingUserInfo$.subscribe((userInfo: EUAV3UserInfo) => {
        try {
          const shortName = userInfo?.shortName;
          if (shortName) {
            const context = { kind: 'user', key: shortName };
            this.launchDarkly = initialize(this.config.app.ldClientSideId, context, {
              bootstrap: 'localStorage',
              streaming: false,
            });
            this.launchDarkly.on('initialized', this.updateFeatureFlags);
          }
        } catch (e) {
          console.error(e);
        }
      });
    }
  }

  private readonly updateFeatureFlags = (): void => {
    const flags = this.launchDarkly.allFlags() as AppFeatureFlags;
    if (flags && Object.keys(flags).length > 0) {
      this.featureFlagsSubject$.next(flags);
    }
  };

  public getFeatureFlags = () => {
    if (environment.ci) {
      this.featureFlagsSubject$.next(DEFAULT_FEATURE_FLAGS);
    }
    return this.featureFlagsSubject$.asObservable();
  };
}
